import { INTEGRATION_FILTER_PARAM } from '@MyAccess/organisms/filters/IntegrationFilterAsync';
import { STATUS_FILTER_PARAM } from '@MyAccess/organisms/filters/StatusFilter';
import { REFETCH_DELAY, SEARCH_PARAM } from '@MyAccess/utils/consts';
import { PaginatedClientResponseModelAccessSessionsGroupClientModel } from '@client-app-sdk';
import useListAccessSessionGroups from '@client-app-sdk/hooks/access-sessions/use-list-access-session-groups';
import { API_LIMIT } from '@client-app-sdk/utils/constants';
import { PAGE_PARAM } from '@common/constants';
import { Flags } from '@common/utils/flags/feature-flags';
import { useFlagFixed } from '@common/utils/flags/use-flag-fixed';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

export default function useAccessSessionGroups() {
  const { isEnabled: isJitConnectedEnabled } = useFlagFixed(Flags.JIT_CONNECT);
  const [queryParams, setQueryParams] = useSearchParams();

  const search = queryParams.get(SEARCH_PARAM) ?? undefined;

  const isFiltersOrSearchApplied =
    queryParams.get(STATUS_FILTER_PARAM) || queryParams.get(INTEGRATION_FILTER_PARAM) || search;

  const status = queryParams.get(STATUS_FILTER_PARAM) ?? undefined;
  const integrationId = queryParams.getAll(INTEGRATION_FILTER_PARAM) ?? undefined;

  const [cachedPageData, setCachedPageData] = useState<
    PaginatedClientResponseModelAccessSessionsGroupClientModel | undefined
  >();

  const pageParam = useMemo(() => {
    const page = parseInt(queryParams.get(PAGE_PARAM) || '1', 10);
    return (page - 1) * API_LIMIT;
  }, [queryParams]);

  const { data, isFetched, fetchNextPage, isFetchingNextPage } = useListAccessSessionGroups({
    skip: pageParam,
    refetch: REFETCH_DELAY,
    includeDisconnected: isJitConnectedEnabled,
    search,
    status: status ? [status] : undefined,
    integrationId,
  });

  const reactQueryPageData = useMemo(() => {
    if (!data) return undefined;

    const pageParamIndex = data.pageParams.findIndex((p) => p === pageParam);
    if (pageParamIndex === -1) return undefined;

    return data.pages[pageParamIndex];
  }, [data, pageParam]);

  useEffect(() => {
    if (!reactQueryPageData && pageParam > 0) {
      fetchNextPage({ pageParam });
      return;
    }

    setCachedPageData(reactQueryPageData);
  }, [fetchNextPage, pageParam, reactQueryPageData]);

  const handleOnPageChange = useCallback(
    (newPage: number) => {
      queryParams.set(PAGE_PARAM, String(newPage));
      setQueryParams(queryParams);
    },
    [queryParams, setQueryParams],
  );

  const handleSearch = useCallback(
    (value: string) => {
      queryParams.delete(SEARCH_PARAM);

      if (value) queryParams.set(SEARCH_PARAM, value);

      setQueryParams(queryParams);
    },
    [queryParams, setQueryParams],
  );

  return {
    pageData: reactQueryPageData || cachedPageData,
    isFetched,
    isFetchingNextPage,
    handleOnPageChange,
    handleSearch,
    search,
    isFiltersOrSearchApplied,
  };
}
